<template>
  <LoadingWrapper :state="status">
    <div v-if="profile" class="mb-4 flex flex-row items-center justify-between">
      <div v-if="analysis" class="flex flex-row gap-1">
        <div
          v-for="entry in perspectiveSummaries"
          v-show="entry.numberOfFeedbacks > 0"
          :key="entry.key"
          class="flex flex-row items-center"
        >
          <span class="text-body-large text-primary mr-2 inline-block w-6 text-right font-semibold">
            {{ entry.numberOfLessons }}
          </span>
          <span v-if="entry.key === SELF_PERSPECTIVE_KEY" class="text-body-medium">
            {{ $t('perspectives.self-long', { count: entry.numberOfLessons }) }}
          </span>
          <template v-else>
            <span class="text-body-medium">
              {{ perspectiveLabel(entry.key, 2) }}-Einschätzungen
            </span>
            <BaseMemberAvatars
              v-if="entry.key === firstNonSelfKey && teacherSenders.length > 0"
              size="24"
              :members="teacherSenders"
              class="bg-surface pl-1"
            />
          </template>
        </div>
      </div>
      <BaseTextButton
        v-if="profile.lessons.length > 0"
        icon="filter_list"
        shape="square"
        color="outlined"
        @click="openLessonFilter"
      >
        {{ selectedLessonIds.length }}
        {{ $t('member.kompetenzprofil.lessons', { count: selectedLessonIds.length }) }}
      </BaseTextButton>
      <span v-else class="text-label-medium text-on-surface-variant">
        {{ $t('member.kompetenzprofil.noLessons') }}
      </span>

      <LessonFilterDialog
        :is-open="isOpen"
        :lessons="profile.lessons"
        :teachers="profile.teachers"
        :model-value="dialogLessonIds"
        @update:model-value="handleLessonSelection"
        @close="closeDialog"
      />
    </div>
    <LoadingWrapper v-if="profile && !analysis" :state="analysisStatus" />
    <EvaluationProfileAnalysis
      v-else-if="analysis && scale && subject"
      :subject="subject"
      :skills="skills"
      :scale="scale"
      :texts="[]"
      :questions="questions"
      :topic-perspectives="topicPerspectives"
      :skill-visualization="skillVisualization"
      :show-blindspots="true"
    />
  </LoadingWrapper>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import BaseMemberAvatars from '@/core/components/base/avatar/BaseMemberAvatars.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import EvaluationProfileAnalysis from '@/core/components/analytics/EvaluationProfileAnalysis.vue'
  import { SkillVisualization } from '@/core/models/feedback-topic'
  import { SELF_PERSPECTIVE, SELF_PERSPECTIVE_KEY } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { TEACHER_PERSPECTIVE } from '../models/kompetenzprofil'
  import { useMemberKompetenzprofil } from '../composables/use-member-kompetenzprofil'
  import {
    asMember,
    chartScale,
    toPerspectiveSummaries,
    toQuestionAnalyses,
    toSkillAnalyses,
  } from '../utils/kompetenzprofil-analysis'
  import LessonFilterDialog from './LessonFilterDialog.vue'

  const { t } = useI18n()
  const {
    member,
    profile,
    analysis,
    status,
    analysisStatus,
    selectedLessonIds,
    isOpen,
    openDialog,
    closeDialog,
    applySelectedLessons,
  } = useMemberKompetenzprofil()

  const dialogLessonIds = ref<string[]>([])
  const skillVisualization = [SkillVisualization.MenonChart]
  const topicPerspectives = [SELF_PERSPECTIVE, TEACHER_PERSPECTIVE]

  const scale = computed(() => (profile.value ? chartScale(profile.value) : null))
  const skills = computed(() => (analysis.value ? toSkillAnalyses(analysis.value) : []))
  const questions = computed(() => (analysis.value ? toQuestionAnalyses(analysis.value) : []))
  const perspectiveSummaries = computed(() =>
    analysis.value ? toPerspectiveSummaries(analysis.value) : [],
  )

  const subject = computed(() => {
    if (profile.value?.member) {
      return asMember(profile.value.member)
    }
    if (!member.value) {
      return null
    }
    return asMember(member.value)
  })

  const teacherSenders = computed(() => {
    const memberId = profile.value?.member.id
    return (analysis.value?.feedbackSenders ?? []).filter((sender) => sender.id !== memberId)
  })

  const firstNonSelfKey = computed(() => {
    return perspectiveSummaries.value.find(
      (entry) => entry.key !== SELF_PERSPECTIVE_KEY && entry.numberOfFeedbacks > 0,
    )?.key
  })

  const perspectiveLabel = (key: string, count: number) =>
    resolvePerspectiveLabel(key, topicPerspectives, {
      kind: 'nameLong',
      count,
      selfLabel: t('perspectives.self'),
    })

  const openLessonFilter = () => {
    dialogLessonIds.value = [...selectedLessonIds.value]
    openDialog()
  }

  const handleLessonSelection = (lessonIds: string[]) => {
    dialogLessonIds.value = [...lessonIds]
    applySelectedLessons(lessonIds)
  }
</script>
